import { type ReactNode, useCallback, useState } from 'react';
import TextArea from '@alltrails/denali/components/TextArea';
import Modal from '@alltrails/modal/components/Modal/Modal';
import styles from './FeedbackModal.module.scss';

/** Shared by every open-text feedback submission, so they all cap the same. */
export const FEEDBACK_CHARACTER_LIMIT = 1000;

export type FeedbackModalCopy = {
  cancelButton: ReactNode;
  submitButton: ReactNode;
  subtitle?: ReactNode;
  textAreaLabel: string;
  textAreaPlaceholder: string;
  title: string;
};

type FeedbackModalProps = {
  copy: FeedbackModalCopy;
  isOpen: boolean;
  onClose: () => void;
  /** Receives the trimmed feedback. The modal clears its text but leaves closing to the caller. */
  onSubmit: (feedback: string) => void;
};

const FeedbackModal = ({ copy, isOpen, onClose, onSubmit }: FeedbackModalProps) => {
  const [feedbackText, setFeedbackText] = useState('');
  // Flags an empty box only once it has been typed in, so it doesn't open in an error state
  const [hasEdited, setHasEdited] = useState(false);
  const trimmedFeedback = feedbackText.trim();

  const onChange = useCallback((value: string) => {
    setFeedbackText(value);
    setHasEdited(true);
  }, []);

  const submitFeedback = useCallback(() => {
    onSubmit(trimmedFeedback);
    setFeedbackText('');
    setHasEdited(false);
  }, [onSubmit, trimmedFeedback]);

  return (
    <Modal
      buttons={{
        primaryButton: {
          text: copy.submitButton,
          onClick: submitFeedback,
          disabled: !trimmedFeedback || feedbackText.length > FEEDBACK_CHARACTER_LIMIT
        },
        flatButton: {
          text: copy.cancelButton,
          onClick: onClose
        }
      }}
      isOpen={isOpen}
      onCloseRequest={onClose}
      size="md"
      subtitle={copy.subtitle}
      title={copy.title}
    >
      <TextArea
        characterLimit={FEEDBACK_CHARACTER_LIMIT}
        className={styles.textArea}
        hideLabel
        labelText={copy.textAreaLabel}
        onChange={onChange}
        placeholder={copy.textAreaPlaceholder}
        required={hasEdited}
        rows={4}
        value={feedbackText}
      />
    </Modal>
  );
};

export default FeedbackModal;
